import {
  of as observableOf,
  combineLatest as observableCombineLatest,
  Observable,
  of,
  takeUntil,
  Subject,
} from 'rxjs';

import {
  mergeMap,
  catchError,
  first,
  map,
  distinctUntilChanged,
  shareReplay,
  filter,
} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  DOCUMENT,
} from '@angular/core';
import {Router, NavigationEnd} from '@angular/router';
import {APPCONFIG, isTrialExpired, getTrialDaysRemaining} from './config';
import {LayoutService} from './layout/layout.service';

import {CacheService} from './cache/cache.service';

import tinycolor from 'tinycolor2';
import {UIConfiguration} from './cache/uiconfiguration/uiconfiguration';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {EventMessage, EventType, InteractionStatus, RedirectRequest} from '@azure/msal-browser';
import {
  argbFromHex,
  hexFromArgb,
  themeFromSourceColor,
  TonalPalette,
} from '@material/material-color-utilities';
import {Endpoint} from './layout/endpoint';
import {environment} from '../environments/environment';
import {
  autoLoginRedirect,
  hasInstallPage,
  isEntraAuth,
  showsEndpointSwitcher,
} from './core/deployment';

export interface Color {
  name: string;
  hex: string;
  darkContrast: boolean;
}

type WithStylesheet = typeof globalThis & {
  [stylesheetName: string]: CSSStyleSheet | undefined;
};

type m3ColorsFromPaletteConfig = {
  primary: {hex: string; tone: number}[];
  secondary: {hex: string; tone: number}[];
  tertiary: {hex: string; tone: number}[];
  neutral: {hex: string; tone: number}[];
  neutralVariant: {hex: string; tone: number}[];
  error: {hex: string; tone: number}[];
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  public AppConfig: any;
  isIframe = false;

  protected readonly environment = environment;

  uiconfiguration: Observable<string>;
  trialDaysRemaining: number | null = null;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private broadcastService: MsalBroadcastService,
    private authService: MsalService,
    private router: Router,
    private cacheService: CacheService,
    private layoutService: LayoutService,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document,
  ) {
    document.body.classList.add('theme-' + APPCONFIG.theme);
    this.layoutService.themeState$.subscribe((darkMode: boolean) => {
      if (darkMode) {
        document.body.classList.remove('theme-' + APPCONFIG.theme);
        document.body.classList.add('theme-dark');
      } else {
        document.body.classList.remove('theme-dark');
        document.body.classList.add('theme-' + APPCONFIG.theme);
      }
    });

    this.layoutService.loadingState$.subscribe(state => {
      this.changeDetector.markForCheck();
    });

    // Always show startup state initially
    this.layoutService.updateLoadingState('startup');
    this.uiconfiguration = this.layoutService.loadingState$;

    if (isTrialExpired()) {
      this.layoutService.updateLoadingState('trialexpired');
      return;
    }
    this.trialDaysRemaining = getTrialDaysRemaining();

    // Windows authentication needs no login step, initialize immediately
    if (!isEntraAuth()) {
      this.initIDApp();
    }
  }

  private handleFinalError(error) {
    this.ngZone.run(() => {
      this.layoutService.updateLoadingState('identitydirector');
    });
    if (error.status === 401 || error.status === 400) {
      this.router.navigate(['/401']);
    } else {
      this.router.navigate(['/500']);
    }
    return observableOf(null);
  }

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;

    this.AppConfig = APPCONFIG;
    // Material 3 color palette; based on https://github.com/Rrothschild18/runtime-angular-material
    this.m3ThemeFromSelectedColor(APPCONFIG.defaultColor);

    this.ngZone.runOutsideAngular(() => {
      // Scroll to top on route change
      this.router.events.subscribe(evt => {
        if (!(evt instanceof NavigationEnd)) {
          return;
        }
        this.ngZone.run(() => {
          window.scrollTo(0, 0);
        });
      });
    });

    if (isEntraAuth()) {
      this.broadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
          takeUntil(this._destroying$),
        )
        .subscribe((result: EventMessage) => {
          console.log('Login successful');
          this.proceedAfterAuthentication();
        });

      // Event-Listener für Login-Fehler
      this.broadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
          takeUntil(this._destroying$),
        )
        .subscribe((result: EventMessage) => {
          console.error('Auto-Login fehlgeschlagen', result.error);
          this.router.navigate(['/401']);
          // Optional: Retry-Mechanismus oder Fehlermeldung anzeigen
        });

      // Event-Listener für Logout-Success
      this.broadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
          takeUntil(this._destroying$),
        )
        .subscribe((result: EventMessage) => {
          console.log('Logout erfolgreich');
          this.cacheService.clearCache();
          this.layoutService.updateLoadingState('startup');
        });

      // Event-Listener für Logout-Failure
      this.broadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_FAILURE),
          takeUntil(this._destroying$),
        )
        .subscribe((result: EventMessage) => {
          console.error('Logout fehlgeschlagen', result.error);
          // Clear cache anyway to ensure clean state
          this.cacheService.clearCache();
        });

      // Überwachung des Interaktionsstatus
      this.broadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
          takeUntil(this._destroying$),
        )
        .subscribe(() => {
          this.checkAndSignIn();
        });
    }
  }

  /**
   * Checks if user is logged in and handles initialization based on auth state
   */
  checkAndSignIn() {
    const activeAccount = this.authService.instance.getActiveAccount();
    const allAccounts = this.authService.instance.getAllAccounts();

    if (!activeAccount && allAccounts.length === 0) {
      // No accounts found - manually trigger login for non-OnPrem environments
      console.log('No accounts found, triggering login redirect');

      if (autoLoginRedirect()) {
        // Manually trigger the MSAL login redirect
        this.authService
          .loginRedirect({
            scopes: ['user.read'],
            redirectUri: window.location.origin,
          })
          .subscribe({
            next: () => {
              console.log('Login redirect initiated');
            },
            error: error => {
              console.error('Login redirect failed:', error);
              this.router.navigate(['/401']);
            },
          });
      }
    } else if (!activeAccount && allAccounts.length > 0) {
      // Account exists but not active - activate it
      this.authService.instance.setActiveAccount(allAccounts[0]);
      this.proceedAfterAuthentication();
    } else {
      // User is already logged in
      this.proceedAfterAuthentication();
    }
  }

  /**
   * Proceed with app initialization after successful authentication
   */
  private proceedAfterAuthentication() {
    if (showsEndpointSwitcher()) {
      this.initApp();
    } else {
      this.initIDApp();
    }
  }

  /**
   * Führt automatischen Login durch
   */
  performAutoLogin() {
    console.log('Starte automatischen Login...');

    // Check if we're already in a redirect flow to prevent loops
    const isRedirect =
      this.authService.instance.getAllAccounts().length > 0 ||
      sessionStorage.getItem('msal.interaction.status') !== null;

    if (isRedirect) {
      console.log('Already in authentication flow, skipping auto-login');
      return;
    }

    // Only attempt silent login, don't automatically show login prompt
    this.authService
      .ssoSilent({
        scopes: ['user.read'],
        loginHint: this.authService.instance.getActiveAccount()?.username,
      })
      .subscribe({
        next: result => {
          console.log('Silent login successful', result);
          this.proceedAfterAuthentication();
        },
        error: error => {
          console.log('Silent login failed', error);
          // Don't automatically redirect to login - let MsalGuard handle it
          // This prevents the redirect loop
        },
      });
  }

  /**
   * Seite neu laden (für Fehlerbehandlung)
   */
  reloadPage() {
    window.location.reload();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  connectToEndpoint(endpoint: {name: string; api: string; cloud: boolean}) {
    this.layoutService.updateEndpoint(
      new Endpoint(endpoint.cloud ? 'idservice' : 'mim', endpoint.api, endpoint.name),
    );

    // Set loading state to startup
    this.ngZone.run(() => {
      this.layoutService.updateLoadingState('startup');
      this.changeDetector.detectChanges();
    });

    // Delay the actual initialization to allow the loader to render
    setTimeout(() => {
      this.initIDApp();
    }, 100); // Small delay to ensure UI updates
  }

  initApp() {
    this.ngZone.run(() => {
      this.layoutService.updateLoadingState('environmentswitch');
      this.changeDetector.detectChanges(); // Force immediate detection
    });
  }
  initIDApp() {
    if (isTrialExpired()) {
      this.layoutService.updateLoadingState('trialexpired');
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      // Refresh all config data and wait for it to complete
      this.cacheService
        .refreshAllData()
        .pipe(
          first(),
          catchError(error => {
            console.error('Failed to load application data:', error);
            return this.handleFinalError(error);
          }),
        )
        .subscribe((success: boolean) => {
          if (!success) {
            console.error('Failed to load application data');
            return;
          }

          // Now that all data is loaded, proceed with initialization

          const obs: Array<Observable<any>> = [];
          obs.push(
            this.cacheService.getNavigationBarConfiguration().pipe(
              first(),
              catchError(() => {
                return of(null);
              }),
            ),
          );
          obs.push(
            this.cacheService.getUIConfiguration().pipe(
              first(),
              catchError(() => {
                return of(null);
              }),
            ),
          );
          this.cacheService
            .getCurrentUser()
            .pipe(
              first(),
              catchError(error => this.handleFinalError(error)),
              mergeMap(currentuser => {
                if (currentuser != null) {
                  if (currentuser.darkmode) {
                    this.layoutService.updateDarkMode(currentuser.darkmode);
                  }
                  return currentuser.administrator.pipe(first());
                }
              }),
            )
            .subscribe((administrator: boolean) => {
              observableCombineLatest(obs)
                .pipe(
                  map(([navbar, uiconf]) => {
                    if (administrator != null && navbar != null && uiconf != null) {
                      if (uiconf.maintenancemode != null && uiconf.maintenancemode === true) {
                        if (!administrator) {
                          this.router.navigate(['/maintenance']);
                        }
                      }
                      this.ngZone.run(() => {
                        this.layoutService.updateLoadingState('identitydirector');
                      });
                    } else if (administrator != null && uiconf == null) {
                      if (hasInstallPage() && administrator) {
                        this.layoutService.updateLoadingState('identitydirector');
                        this.router.navigate(['/install']);
                      }
                    }
                  }),
                  catchError(error => {
                    if (hasInstallPage() && administrator) {
                      this.layoutService.updateLoadingState('identitydirector');
                      this.router.navigate(['/install']);
                    } else {
                      return this.handleFinalError(error);
                    }
                  }),
                )
                .subscribe(() => {
                  this.changeDetector.detectChanges();
                });
            });

          // Subscribe to UI configuration for theme updates
          this.cacheService
            .getUIConfiguration()
            .pipe(first(), distinctUntilChanged(), shareReplay(1))
            .subscribe((uiconf: UIConfiguration) => {
              // Material 3 theme generation only - removed M2 variable generation
              if (uiconf && uiconf.primarycolor !== null && uiconf.primarycolor !== undefined) {
                this.m3ThemeFromSelectedColor(uiconf.primarycolor);
              }
            });
        }); // End of refreshAllData subscription
    });
  }

  private computeColors(hex: string): Color[] {
    return [
      this.getColorObject(tinycolor(hex).lighten(52), '50'),
      this.getColorObject(tinycolor(hex).lighten(37), '100'),
      this.getColorObject(tinycolor(hex).lighten(26), '200'),
      this.getColorObject(tinycolor(hex).lighten(12), '300'),
      this.getColorObject(tinycolor(hex).lighten(6), '400'),
      this.getColorObject(tinycolor(hex), '500'),
      this.getColorObject(tinycolor(hex).darken(6), '600'),
      this.getColorObject(tinycolor(hex).darken(12), '700'),
      this.getColorObject(tinycolor(hex).darken(18), '800'),
      this.getColorObject(tinycolor(hex).darken(24), '900'),
      this.getColorObject(tinycolor(hex).lighten(50).saturate(30), 'A100'),
      this.getColorObject(tinycolor(hex).lighten(30).saturate(30), 'A200'),
      this.getColorObject(tinycolor(hex).lighten(10).saturate(15), 'A400'),
      this.getColorObject(tinycolor(hex).lighten(5).saturate(5), 'A700'),
    ];
  }

  private getColorObject(value, name): Color {
    const c = tinycolor(value);
    return {
      name: name,
      hex: c.toHexString(),
      darkContrast: c.isLight(),
    };
  }

  private m3ThemeFromSelectedColor(color?: string): void {
    const default_tones = [0, 10, 20, 25, 30, 35, 40, 50, 60, 70, 80, 90, 95, 98, 99, 100];
    const neutral_tones = [
      0, 10, 20, 25, 30, 35, 40, 50, 60, 70, 80, 90, 95, 98, 99, 100, 4, 6, 12, 17, 22, 24, 87, 92,
      94, 96,
    ];
    const theme = themeFromSourceColor(argbFromHex(color));

    const colors = Object.entries(theme.palettes).reduce(
      (acc: any, curr: [string, TonalPalette]) => {
        const tones = curr[0] === 'neutral' ? neutral_tones : default_tones;
        const hexColors = tones.map(tone => ({
          tone,
          hex: hexFromArgb(curr[1].tone(tone)),
        }));

        return {...acc, [curr[0]]: hexColors};
      },
      {},
    );

    this.createM3CustomProperties(colors);
  }

  private createM3CustomProperties(colorsFromPaletteConfig: m3ColorsFromPaletteConfig) {
    let styleString = ':root,:host{';

    // Generate palette colors
    for (const [key, palette] of Object.entries(colorsFromPaletteConfig)) {
      const kebabCase = key.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
      palette.forEach(({hex, tone}) => {
        styleString += `--${kebabCase}-${tone}:${hex};`;
        // Also add as md-ref-palette for reference
        styleString += `--md-ref-palette-${kebabCase}-${tone}:${hex};`;
      });
    }

    // Generate M3 system color tokens for light theme
    // Primary colors
    styleString += `--md-sys-color-primary:var(--primary-40);`;
    styleString += `--md-sys-color-on-primary:var(--primary-100);`;
    styleString += `--md-sys-color-primary-container:var(--primary-90);`;
    styleString += `--md-sys-color-on-primary-container:var(--primary-10);`;

    // Secondary colors
    styleString += `--md-sys-color-secondary:var(--secondary-40);`;
    styleString += `--md-sys-color-on-secondary:var(--secondary-100);`;
    styleString += `--md-sys-color-secondary-container:var(--secondary-90);`;
    styleString += `--md-sys-color-on-secondary-container:var(--secondary-10);`;

    // Tertiary colors
    styleString += `--md-sys-color-tertiary:var(--tertiary-40);`;
    styleString += `--md-sys-color-on-tertiary:var(--tertiary-100);`;
    styleString += `--md-sys-color-tertiary-container:var(--tertiary-90);`;
    styleString += `--md-sys-color-on-tertiary-container:var(--tertiary-10);`;

    // Error colors
    styleString += `--md-sys-color-error:var(--error-40);`;
    styleString += `--md-sys-color-on-error:var(--error-100);`;
    styleString += `--md-sys-color-error-container:var(--error-90);`;
    styleString += `--md-sys-color-on-error-container:var(--error-10);`;

    // Surface colors
    styleString += `--md-sys-color-surface:var(--neutral-98);`;
    styleString += `--md-sys-color-on-surface:var(--neutral-10);`;
    styleString += `--md-sys-color-surface-variant:var(--neutral-variant-90);`;
    styleString += `--md-sys-color-on-surface-variant:var(--neutral-variant-30);`;
    styleString += `--md-sys-color-surface-container:var(--neutral-94);`;

    // Outline colors
    styleString += `--md-sys-color-outline:var(--neutral-variant-50);`;
    styleString += `--md-sys-color-outline-variant:var(--neutral-variant-80);`;

    // RGB versions for opacity calculations
    styleString += `--md-sys-color-on-surface-rgb:0, 0, 0;`; // Will be overridden by theme

    styleString += '}';

    // Dark theme overrides
    styleString += `.theme-dark,:host-context(.theme-dark){`;
    styleString += `--md-sys-color-primary:var(--primary-80);`;
    styleString += `--md-sys-color-on-primary:var(--primary-20);`;
    styleString += `--md-sys-color-primary-container:var(--primary-30);`;
    styleString += `--md-sys-color-on-primary-container:var(--primary-90);`;
    styleString += `--md-sys-color-secondary:var(--secondary-80);`;
    styleString += `--md-sys-color-on-secondary:var(--secondary-20);`;
    styleString += `--md-sys-color-secondary-container:var(--secondary-30);`;
    styleString += `--md-sys-color-on-secondary-container:var(--secondary-90);`;
    styleString += `--md-sys-color-surface:var(--neutral-6);`;
    styleString += `--md-sys-color-on-surface:var(--neutral-90);`;
    styleString += `--md-sys-color-surface-container:var(--neutral-12);`;
    styleString += `--md-sys-color-on-surface-rgb:255, 255, 255;`;
    styleString += '}';

    this.applyStylesheet(styleString);
  }

  private applyStylesheet(content: string, name = 'm3-theme') {
    let sheet = (globalThis as WithStylesheet)[name];

    if (!sheet) {
      sheet = new CSSStyleSheet();
      (globalThis as WithStylesheet)[name] = sheet;
      this.document.adoptedStyleSheets.push(sheet);
    }

    sheet.replaceSync(content);
  }
}
